import { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Loader2, Check } from 'lucide-react';
import { API } from '@/App';
import { SpotlightCreative } from '@/components/spotlights/SpotlightCreative';

/** Public rate card — the page he can send people to. */
export default function AdvertisePage() {
  const [slots, setSlots] = useState([]);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', company: '', slot: '', message: '' });

  useEffect(() => {
    axios.get(`${API}/advertise/rate-card`)
      .then((res) => setSlots(res.data.slots || []))
      .catch(() => setSlots([]));
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const valid = form.name.trim() && /\S+@\S+\.\S+/.test(form.email);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/advertise/enquiry`, form);
      setSent(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send that — please email us instead');
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="min-h-screen px-6 py-20 sm:px-12 lg:px-24"
      style={{ background: 'linear-gradient(to bottom, #0a0e1a 0%, #1a2332 60%, #2d3748 100%)' }}
      data-testid="advertise-page"
    >
      <div className="mx-auto max-w-5xl">
        <p className="mb-4 text-xs uppercase tracking-[0.3em] text-amber-300/80">Advertise with us</p>
        <h1 className="mb-6 max-w-2xl font-serif text-4xl leading-tight text-white sm:text-5xl lg:text-6xl">
          Reach people who are actually looking for you
        </h1>
        <p className="mb-16 max-w-xl text-base leading-relaxed text-gray-300">
          Our members are here to do inner work — they arrive already interested in wellbeing,
          practice and craft. A handful of placements are open across the marketplace, workshops and
          booking pages. Everything is reviewed by hand, so the wall never turns into clutter.
        </p>

        {slots.length > 0 && (
          <>
            <h2 className="mb-8 text-base font-semibold text-white md:text-lg">
              What&apos;s available
            </h2>
            <div className="mb-20 grid gap-8 sm:grid-cols-2 lg:grid-cols-3" data-testid="rate-card-slots">
              {slots.map((slot) => (
                <div key={slot.key} data-testid={`rate-card-slot-${slot.key}`}>
                  <SpotlightCreative p={{}} variant={slot.variant} theme={slot.theme || 'dark'} />
                  <h3 className="mt-4 text-sm font-semibold text-white">{slot.label}</h3>
                  {slot.image?.note && (
                    <p className="mt-1 text-xs leading-relaxed text-white/45">{slot.image.note}</p>
                  )}
                  <p className="mt-3 text-sm text-amber-300">
                    {slot.price_per_month > 0
                      ? `$${slot.price_per_month.toFixed(0)} / month`
                      : `$${slot.price_per_day.toFixed(2)} / day`}
                  </p>
                  {slot.image && (
                    <p className="mt-1 text-xs text-white/35">
                      Artwork {slot.image.width} × {slot.image.height}px
                    </p>
                  )}
                </div>
              ))}
            </div>
          </>
        )}

        <div className="max-w-lg rounded-3xl border border-white/10 bg-white/5 p-8 backdrop-blur-xl">
          {sent ? (
            <div data-testid="advertise-enquiry-sent">
              <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-1.5 text-sm text-emerald-200">
                <Check className="h-4 w-4" /> Sent
              </div>
              <h2 className="mb-3 font-serif text-2xl text-white">We&apos;ll be in touch</h2>
              <p className="text-sm leading-relaxed text-gray-300">
                One of us will email you within a couple of days with the spots that are free and a
                private link where you can build your ad — no account needed.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="advertise-enquiry-form">
              <h2 className="text-base font-semibold text-white md:text-lg">Ask about a spot</h2>
              <div>
                <Label className="mb-2 block text-sm text-white/70">Your name</Label>
                <Input value={form.name} onChange={set('name')} data-testid="enquiry-name-input" />
              </div>
              <div>
                <Label className="mb-2 block text-sm text-white/70">Email</Label>
                <Input type="email" value={form.email} onChange={set('email')} data-testid="enquiry-email-input" />
              </div>
              <div>
                <Label className="mb-2 block text-sm text-white/70">
                  Business <span className="text-white/35">— optional</span>
                </Label>
                <Input value={form.company} onChange={set('company')} data-testid="enquiry-company-input" />
              </div>
              <div>
                <Label className="mb-2 block text-sm text-white/70">
                  What would you like to promote? <span className="text-white/35">— optional</span>
                </Label>
                <Textarea rows={3} value={form.message} onChange={set('message')} data-testid="enquiry-message-input" />
              </div>
              <Button
                type="submit"
                disabled={!valid || sending}
                className="w-full bg-amber-400 py-6 text-slate-900 transition-colors duration-200 hover:bg-amber-300 disabled:opacity-40"
                data-testid="enquiry-submit-btn"
              >
                {sending ? <Loader2 className="h-5 w-5 animate-spin" /> : 'Send enquiry'}
              </Button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
